import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import { Backdrop, Fade, Button } from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import React from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { getFileDateName } from "../../utils/utils";
import { RowBox } from "../../utils/styledBox";
import { Image } from "@mui/icons-material";
import { useCustomNavigate } from "../../hooks/useCustomNavigate";

interface ImgModalProps {
  src: string;
  alt: string;
  open: boolean;
  onClose: () => void;
  generatedImageUuid?: string; // generatedImageUuid を追加
}

const ImageModal: React.FC<ImgModalProps> = ({ src, alt, open, onClose, generatedImageUuid }) => {
  const { t } = useTranslation();
  const navigate = useCustomNavigate();

  const handleDownloadClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); // クリックイベントの伝播を停止
    try {
      const response = await axios.get(`/api/v1/juno/image/${generatedImageUuid}`, { responseType: "blob" });
      const blob = new Blob([response.data], { type: response.data.type });
      const url = window.URL.createObjectURL(blob);

      let extension = ".png"; // デフォルト拡張子
      if (response.data.type === "image/svg+xml") {
        extension = ".svg";
      } else if (response.data.type === "image/jpeg") {
        extension = ".jpg";
      } else if (response.data.type === "image/png") {
        // デフォルトがpngなので明示的なチェックは不要だが、念のため
        extension = ".png";
      }

      const a = document.createElement("a");
      a.href = url;
      a.download = `juno-${getFileDateName()}${extension}`;
      document.body.appendChild(a); // DOMに追加
      a.click();
      document.body.removeChild(a); // DOMから削除

      // リソース解放
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error("画像の取得に失敗しました:", error);
      return { success: false };
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{
        maxWidth: "100%",
        maxHeight: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={onClose}
    >
      <Fade in={open}>
        <Box
          onClick={(e) => {
            e.stopPropagation(); // モーダル内のクリックがモーダル自体を閉じないようにする
          }}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.default",
            boxShadow: 24,
            px: { xs: 2, md: 4 },
            py: 2,
            outline: 0,
            borderRadius: 1,
          }}
        >
          <RowBox justifyContent={"flex-end"} mb={2}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </RowBox>

          <Box
            component="img"
            src={src}
            alt={alt}
            sx={{
              maxWidth: "100%",
              maxHeight: "calc(100vh - 112px)",
              objectFit: "contain",
              display: "block",
            }}
          />
          {generatedImageUuid && (
            <RowBox justifyContent={"flex-end"} mt={2} gap={1}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Image />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/juno/image-generator", {
                    state: {
                      imageUuid: generatedImageUuid,
                    },
                  });
                }}
              >
                STUDIO JUNO
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownloadClick(e);
                }}
              >
                {t("common.download")}
              </Button>
            </RowBox>
          )}
        </Box>
      </Fade>
    </Modal>
  );
};

export default ImageModal;
